// External
import _, { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";

// Internal
import { useCallbackRef } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import { fontStyles } from "@toddle-edu/ds-theme";

// Relative
import classes from "./DynamicPromptInput.scss";
import { CONTEXT_FIELDS } from "../../AiQuestionGeneratorConstants";
import {
  ADDITIONAL_INSTRUCTIONS_ID,
  EDITABLE_CONTENT,
  CUSTOM_INSTRUCTIONS_ID,
  getSerializedData,
  getInstructionDataHtmlString,
  loopingChildNodes,
  focusElementWithCaret,
  getEditableElements,
  isCaretAtEdge,
  focusEditableArea,
} from "./modules/DynamicPromptInputUtils";

const { CF_AI_INSTRUCTIONS } = CONTEXT_FIELDS;

const styles = {
  uiContentEditable: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    overflowY: "auto",
    ...fontStyles.medium,
  },
};

/**
 *
 * @param {*} params
 * @returns Dynamic preset prompt input
 */
const DynamicPromptInput = ({
  t,
  onChange,
  instruction,
  setActiveInput,
  selectedPrompt,
  setSelectedPrompt,
}) => {
  const [contentEditableRef, setContentEditableRef] = useCallbackRef(null);
  const [value, setValue] = useState("");

  // * Updating active input on focus
  const handleInputFocus = e => setActiveInput(e.target.id);

  useEffect(() => {
    if (contentEditableRef?.current) {
      contentEditableRef.current.setAttribute("contenteditable", "false");
    }
  }, [instruction]);

  /**
   *
   * @param {*} args
   * @returns Updated data in Json formate
   */
  const updateInputField = args => {
    if (isEmpty(args)) {
      // * if preset prompt is cleared, than set to normal state
      setValue("");
      setActiveInput(ADDITIONAL_INSTRUCTIONS_ID);
      setSelectedPrompt(null);
      onChange(CF_AI_INSTRUCTIONS, { selectedPrompt: null, data: "" });
      return;
    }

    // * Return if not valid ref
    if (!contentEditableRef?.current) return;

    const serializedData = getSerializedData(contentEditableRef);
    onChange(CF_AI_INSTRUCTIONS, { selectedPrompt, data: serializedData });
    setValue(args);

    // * Removing extra <br> tag if present in additional instructions
    const element = document?.querySelector(`#${ADDITIONAL_INSTRUCTIONS_ID}`);
    if (element && element.innerHTML === "<br>") {
      setTimeout(() => {
        element.innerHTML = "";
        element?.focus();
      }, 0);
    }
  };

  useEffect(() => {
    if (!selectedPrompt) return;

    const htmlString = getInstructionDataHtmlString(
      t,
      instruction,
      selectedPrompt
    );
    setValue(htmlString);

    const timeout = setTimeout(() => {
      // * Updating template in meta data after 1000ms
      updateInputField(htmlString);

      // * Adding focus event to all editable span
      loopingChildNodes(contentEditableRef, element => {
        element.addEventListener("focus", handleInputFocus);
      });

      // * Programmatically focusing the first editable span
      const firstEditableSpan = contentEditableRef.current.querySelector(
        `[id^="${EDITABLE_CONTENT}"]`
      );
      if (firstEditableSpan) {
        firstEditableSpan.focus();
        // Place cursor at the end of text
        if (firstEditableSpan.innerText) {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(firstEditableSpan);
          range.collapse(false); // collapse to end
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }, 500);

    return () => {
      clearTimeout(timeout);

      // * Removing focus event
      loopingChildNodes(contentEditableRef, element => {
        element.removeEventListener("focus", handleInputFocus);
      });
    };
  }, [selectedPrompt]);

  const handleKeyDown = e => {
    // Backspace handling
    if (
      e?.key === "Backspace" &&
      e?.target?.textContent === "" &&
      e?.target?.id === ADDITIONAL_INSTRUCTIONS_ID
    ) {
      // * if preset prompt is cleared, than set to normal state
      setValue("");
      setActiveInput(ADDITIONAL_INSTRUCTIONS_ID);
      setSelectedPrompt(null);
      onChange(CF_AI_INSTRUCTIONS, { selectedPrompt: null, data: "" });
      return;
    }

    // Enter handling
    if (e?.key === "Enter") {
      if (
        !_.isEmpty(e?.target?.textContent) &&
        e?.target?.id === ADDITIONAL_INSTRUCTIONS_ID
      ) {
        // if additional instruction is not empty, then allow enter
        return;
      }
      if (
        !_.isEmpty(e?.target?.textContent) &&
        _.startsWith(e?.target?.id, EDITABLE_CONTENT)
      ) {
        // if editable content is not empty, then allow enter and move to additional instructions
        setActiveInput(ADDITIONAL_INSTRUCTIONS_ID);
        const additionalInstructionsSpan = contentEditableRef?.current?.querySelector(
          `#${ADDITIONAL_INSTRUCTIONS_ID}`
        );
        focusElementWithCaret(additionalInstructionsSpan, "end");
      }
      // to prevent creating a new line when enter is pressed
      e.preventDefault();
      return;
    }

    // Arrow navigation across editable segments
    if (e?.key === "ArrowRight" || e?.key === "ArrowLeft") {
      const target = e?.target;
      if (!target || !(target instanceof HTMLElement)) return;

      const elements = getEditableElements(contentEditableRef);
      if (elements.length === 0) return;

      const currentIndex = elements.findIndex(el => el.id === target.id);
      if (currentIndex === -1) return;

      if (e.key === "ArrowRight" && isCaretAtEdge(target, "end")) {
        const next = elements[currentIndex + 1];
        if (next) {
          e.preventDefault();
          focusElementWithCaret(next, "start");
        }
      }

      if (e.key === "ArrowLeft" && isCaretAtEdge(target, "start")) {
        const prev = elements[currentIndex - 1];
        if (prev) {
          e.preventDefault();
          focusElementWithCaret(prev, "end");
        }
      }
    }
  };

  if (!selectedPrompt) return null;

  return (
    <div
      className={classes.contentEditable}
      onClick={e => focusEditableArea(e, contentEditableRef)}
      onKeyDown={e => {
        if (e.target === e.currentTarget) {
          onEnterOrSpaceKeyDown(e, () =>
            focusEditableArea(null, contentEditableRef)
          );
        }
      }}
      tabIndex={0}
    >
      <UIBaseContentEditable
        id={CUSTOM_INSTRUCTIONS_ID}
        value={value}
        updateInputField={updateInputField}
        style={styles.uiContentEditable}
        minRows={1}
        maxRows={4}
        disabled={false}
        onKeyDown={handleKeyDown}
        updateInputRef={setContentEditableRef}
        shouldParseInPlainText={false}
      />
    </div>
  );
};

export default DynamicPromptInput;
